import Twemoji from "react-twemoji";
import { ScopeType } from "sockapi/scope-types";
import { SockAPIScopeState } from "sockapi/scope-state";
import { HintResponseType, HintStrength } from "sockapi/scope-specs/hint-spec";
import { LongDate } from "components/dates";

import "./HintDisplay.css";
import { useHintAdminQueueTaskContext } from "components/AdminQueue/admin-queue-hint";
import Tooltip from "components/Tooltip";

export const HINT_STRENGTH_ICON = {
  [HintStrength.NUDGE]: "▪️",
  [HintStrength.BRIDGE]: "◾️",
  [HintStrength.SLEDGE]: "⬛️",
}; // unused: ◼️

export const HINT_STRENGTH_DESC = {
  [HintStrength.NUDGE]:
    "Give me a nudge. I want to figure out the aha myself, so give me a small hint that’s just enough to get me on the right track.",
  [HintStrength.BRIDGE]: "Default. Give me whatever hint you think is best.",
  [HintStrength.SLEDGE]:
    "Give me a sledgehammer hint. I don’t care about being spoiled, just tell me what to do.",
};

export const HINT_STRENGTH_TOOLTIP =
  "This is more relevant if you’re getting hinted on an aha moment you’re missing. If you e.g., just want us to check your work, then it doesn’t matter what option you pick. No matter what you pick, if the hint you receive doesn’t get you unstuck, you may request a follow-up hint with a different strength.";

function HintDisplay(props: {
  hintId: string;
  hint: SockAPIScopeState<ScopeType.HINT>;
  isAdmin?: boolean;
}) {
  const { hint, isAdmin = false } = props;
  const {
    request: { content, isFollowup, strength, timestamp },
    response,
  } = hint;
  const hintCtx = useHintAdminQueueTaskContext();

  return (
    <div className="hint-content">
      <div>
        <div className="hint-content-row">
          <div className="hint-text-label">Q:</div>
          <pre className="hint-text">{content}</pre>
        </div>
        {!(response !== null && response.type === HintResponseType.OBSOLETE) ? (
          <div className="hint-content-row">
            <div className="hint-text-label">A:</div>
            <pre className="hint-text">
              {response === null ? "(No response yet.)" : response.content}
            </pre>
            {hintCtx && response && (
              <Tooltip
                text={
                  <>
                    Copy this response to your hint response input.{" "}
                    <b>This will wipe your existing response.</b>
                  </>
                }
                className="hint-text-button"
              >
                <button
                  onClick={() => hintCtx.setAnswerResponse(response.content)}
                >
                  <Twemoji>📋</Twemoji>
                </button>
              </Tooltip>
            )}
          </div>
        ) : null}
      </div>
      <div className="hint-metadata">
        <div>
          Asked <LongDate timestamp={timestamp} />
          {response === null ? null : (
            <>
              ; answered <LongDate timestamp={response.timestamp} />
            </>
          )}
        </div>
        <Twemoji>
          {HINT_STRENGTH_ICON[strength]} {HINT_STRENGTH_DESC[strength]}
        </Twemoji>
        {(isFollowup ?? false) ? <div>This was a follow-up hint.</div> : null}
        {response !== null && response.type === HintResponseType.OBSOLETE ? (
          <div>
            {isAdmin
              ? "This hint was marked as obsolete."
              : "Looks like you solved this puzzle before we could send you a response! Your use of the hint was refunded."}
          </div>
        ) : null}
        {response !== null && response.type === HintResponseType.REFUNDED ? (
          <div>
            {isAdmin
              ? "This hint was refunded."
              : "Your use of the hint was refunded."}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default HintDisplay;
